import { Label, LabelConfig, Pipeline, PipelineStep, PipelineTask, TaskOption } from '@telespot/sdk';
import { StepTask } from '@telespot/domain';
import { PipelineEntity } from '../models/pipeline.entity';
import { type ILabelConfig, IEnvironment } from '@telespot/protocols/domain';

export class ProtocolMapper {
  public static toPipelineEntity(
    pipeline: Pipeline,
    steps: PipelineStep[] = [],
    labels: Label[],
    labelConfig?: LabelConfig[]
  ) {
    const tasks: PipelineTask[] = [];

    steps.forEach((step) => {
      const stepCategories = step.params?.categorization ?? [];

      stepCategories.forEach((sc) => {
        const taskLabel = labels.find((l) => l.uuid === sc.category);

        const newTask = {
          id: step.id,
          name: {
            name: taskLabel?.value,
            uuid: sc.category,
            value: { [taskLabel?.lang]: taskLabel?.value },
            new: false,
          },
          type: step.task,
          allowNewOptions: step?.params?.allowNewOptions ?? false,
          enableMosaicView: step?.params?.enableMosaicView ?? false,
          trigger: step.trigger,
          generateFinding: step.generateFinding,
          executor: step?.executor,
          multiple: step?.params?.multiple ?? false,
          counter: step?.params?.counter ?? false,
          _params: step.params,
        };

        const includeOptions = step.task !== StepTask.TEXT && step.task !== StepTask.OCR;

        if (includeOptions) {
          const options = this.getTaskOptions(step.params.options, sc.options, labels, labelConfig);

          newTask['options'] = options;
        }

        tasks.push(newTask);
      });
    });

    return new PipelineEntity({
      id: pipeline.id,
      name: pipeline.name,
      description: pipeline.description,
      tasks,
      env: (pipeline.env as IEnvironment) ?? IEnvironment.CLOUD,
    });
  }

  public static toPipelineSteps(pipeline: PipelineEntity): PipelineStep[] {
    const pipelineSteps: PipelineStep[] = [];
    const isCloudPipeline = pipeline.env === IEnvironment.CLOUD;

    pipeline.tasks.forEach((t, i) => {
      const existingStepTask = pipelineSteps.find((s) => s.task === t.type && ProtocolMapper.taskIsRelatedToTagging(t));

      if (existingStepTask) {
        existingStepTask.params = {
          ...existingStepTask.params,
          ...t._params,
          options: {
            ...existingStepTask.params.options,
            ...(t.options || []).reduce((acc, o) => {
              acc = {
                ...acc,
                [o.uuid]: { thrdisp: o?.thrdis ?? 0 },
              };
              return acc;
            }, {}),
          },
          categorization: [
            ...existingStepTask.params.categorization,
            {
              category: t.name?.uuid,
              options: (t.options || []).map((o) => o?.uuid),
            },
          ],
          multiple: t?.multiple,
          allowNewOptions: t.allowNewOptions,
          enableMosaicView: t.enableMosaicView,
        };
        return;
      }

      const pipelineStep =
        t.id && t.id.includes('new:') === false
          ? (PipelineStep.createWithoutData(t.id) as PipelineStep)
          : new PipelineStep();

      pipelineStep.task = t.type;

      const params = {
        ...t._params,
        options: {
          ...(t.options || []).reduce((acc, o) => {
            acc = {
              ...acc,
              [o.uuid]: { thrdisp: o?.thrdis ?? 0 },
            };
            return acc;
          }, {}),
        },
        categorization: [
          {
            category: t.name?.uuid,
            options: (t.options || []).map((o) => o?.uuid),
          },
        ],
        multiple: t.multiple,
        allowNewOptions: t.allowNewOptions,
        enableMosaicView: t.enableMosaicView,
        counter: t?.counter,
      };

      pipelineStep.params = params;
      pipelineStep.trigger = t.trigger;
      pipelineStep.generateFinding = t.generateFinding;
      pipelineStep.pipeline = Pipeline.createWithoutData(pipeline.id) as Pipeline;

      pipelineStep.changeExecutor(t?.executor);
      pipelineSteps.push(pipelineStep);
    });

    pipelineSteps.forEach((step, index) => (step.order = isCloudPipeline ? 0 : index));

    return pipelineSteps;
  }

  public static getStepsToDelete(currSteps, updatedSteps: PipelineStep[]) {
    return currSteps.filter((s) => !updatedSteps.some((step) => step.id === s.id));
  }

  public static taskIsRelatedToTagging(task: PipelineTask) {
    return task.type === StepTask.POSITION || task.type === StepTask.ROIDETECTION || task.type === StepTask.ROITRACKING;
  }

  public static getTaskOptions(
    optionsWithThresholds: any,
    options: string[],
    labels: Label[],
    labelConfig: LabelConfig[]
  ) {
    return options.reduce((acc, uuid) => {
      const label = labels.find((l) => l.uuid === uuid);
      const config = labelConfig.find((config) => config.label === uuid);

      const { color, alpha } = this._parseColor(config?.color || '');

      if (label) {
        acc.push({
          name: label.value,
          uuid: label.uuid,
          color,
          alpha,
          value: { [label.lang]: label.value },
          configId: config?.id,
          thrdis: optionsWithThresholds[uuid]?.thrdisp ?? 0,
          new: false,
        });
      }

      return acc;
    }, []);
  }

  public static getLabelUuids(steps: PipelineStep[]) {
    const uuids: Array<string> = [];
    steps.forEach((step) => {
      if (!step.params) return;
      if (step.task === StepTask.TEXT || step.task === StepTask.OCR) {
        const category = step.params?.categorization[0]?.category;
        if (category) return uuids.push(category);
      }
      return (step.params?.categorization || []).forEach((c) => {
        uuids.push(c.category, ...c.options);
      });
    });
    return uuids;
  }

  public static filterLabels(labels: Label[], language: string) {
    const filteredLabelsMap = new Map();

    labels.forEach((label) => {
      const existingLabel = filteredLabelsMap.get(label.uuid);

      if (!existingLabel) {
        filteredLabelsMap.set(label.uuid, label);
      } else if (label.lang === language || (existingLabel.lang !== language && label.lang === 'en')) {
        filteredLabelsMap.set(label.uuid, label);
      }
    });

    return Array.from(filteredLabelsMap.values());
  }

  public static toStateLabelConfig(options: TaskOption[], taskId: string): ILabelConfig[] {
    const labelConfigs: ILabelConfig[] = [];

    options.forEach((o) => {
      const alphaHex = Math.round((o.alpha ?? 0.2) * 255)
        .toString(16)
        .padStart(2, '0');
      const hexColorWithAlpha = `#${alphaHex}${o.color.slice(1)}`;

      labelConfigs.push({
        label: o.uuid,
        color: o.color ? hexColorWithAlpha : null,
        configId: o.configId || null,
        taskId,
      });
    });

    return labelConfigs;
  }

  public static buildLabelConfig(labelConfigDetails: ILabelConfig[], methodType): LabelConfig[] {
    const labelConfigs: LabelConfig[] = [];

    const processedLabels = new Set<string>();

    for (const config of labelConfigDetails) {
      if (!config?.label || !config?.color || processedLabels.has(config.label)) continue;

      processedLabels.add(config.label);

      const labelConfigElement = config.configId
        ? (LabelConfig.createWithoutData(config.configId) as LabelConfig)
        : new LabelConfig();

      labelConfigElement.label = config.label;
      labelConfigElement.color = config.color;
      labelConfigElement.methodType = methodType;

      labelConfigs.push(labelConfigElement);
    }

    return labelConfigs;
  }

  private static _parseColor(color: string): {
    color: string;
    alpha: number | null;
  } {
    if (/^#[0-9A-Fa-f]{8}$/.test(color)) {
      return {
        color: `#${color.slice(3)}`,
        alpha: parseInt(color.slice(1, 3), 16) / 255,
      };
    } else {
      return {
        color: '',
        alpha: 0.2,
      };
    }
  }
}
